import React, { createContext, useEffect, useState, useCallback, useRef, PropsWithChildren } from 'react';
import * as Sentry from '@sentry/react';
import invariant from 'tiny-invariant';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { DateTime, Interval } from 'luxon';
import {
  CampaignFormInputs,
  CampaignFields,
  Website,
  BillingMethod,
  CampaignStep,
  CampaignStepState,
  CampaignFormStepDataMap,
  StepStatesByCampaignId,
  GetFieldControls,
  CampaignFormInputsPath,
  UpdateCampaignRequest,
  WebsiteRequestItem,
  CampaignFormField,
  RouteList,
} from 'types';
import { useAuth } from 'hooks/useAuth';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useCampaign } from 'hooks/useCampaign';
import { useSlidingDelay, OnCallOptions, SlidingDelayError } from 'hooks/useSlidingDelay';
import { useGa4 } from 'hooks/useGa4';
import { getNextStep, getStepIndex, getStepStates } from 'utils';
import {
  defaultUpdateRequestBody,
  orderedSteps,
  stepIndices,
  defaultFormValues,
  getFormInputs,
  MIN_DAYS_BEFORE_CAMPAIGN_START,
  campaignSteps,
  focusWebsiteUrl,
  emptyWebsite,
  defaultTargetingGuidanceConfig,
} from 'shared';
import { generatePath, useNavigate } from 'react-router-dom';

export type CampaignFormContext = ReturnType<typeof GetCampaignFormContext>['campaignContext'];

const defaultSaveDelay = 800;

export type CampaignFormProviderProps = {
  /** Will initiate campaign create mode when after first step complete we create the campaign */
  isCreateCampaignFlow?: boolean;
  /** Specifies the starting step the page opens at. Also used in create campaign flow, allow us to specify step to jump to after successful campaign creation after Basic step */
  startStep?: CampaignStep | null;
  /** Save campaign debounce delay */
  saveDelay?: number;
};

/**
 * CampaignFormContext factory.
 * Can be called only in a component scope.
 * CamelCase fn name is to suppress react-hooks/rules-of-hooks eslint rule errors.
 * See options in {@link CampaignFormProviderProps}
 */
export const GetCampaignFormContext = ({
  isCreateCampaignFlow = false,
  startStep,
  saveDelay = defaultSaveDelay,
}: CampaignFormProviderProps) => {
  const { campaignId, updateCampaignMutation, createCampaignMutation, getCampaignQuery, publishCampaignMutation } =
    useCampaign(isCreateCampaignFlow);
  const {
    checkGa4ConnectionMutation,
    isConnectionSuccess,
    setIsConnectionSuccess,
    failureCount,
    getGa4CampaignUrlQuery,
  } = useGa4();
  const currentCampaignId = Number(campaignId);
  const { user } = useAuth();
  const navigate = useNavigate();

  // TODO: only get request error are trapped and propagated to the router. so should be handled others.
  if (getCampaignQuery.error) {
    throw getCampaignQuery.error;
  }

  const [isInitialized, setIsInitialized] = useState(false);
  const [hasInThePastError, setHasInThePastError] = useState(false);
  const [hasCreativesError, setHasCreativesError] = useState(false);
  const [stepsData, setStepsData] = useState(campaignSteps);
  const [activeStep, setActiveStep] = useState(startStep ?? CampaignStep.Basic);
  const isFirstStep = activeStep === orderedSteps.at(0);
  const isLastStep = activeStep === orderedSteps.at(-1);
  const [isPreNavValidation, setIsPreNavValidation] = useState(false);

  // kinda separated interface ref for mutually dependent functions
  const internalRef = useRef({
    setWebsiteEditing,
  });

  const handleChangeStep = (step: CampaignStep, campaignId?: number | null) => {
    if (hasCreativesError) {
      setHasCreativesError(false);
    }

    // scroll to top
    document.getElementById('page-container-root')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    setActiveStep(step);

    // once campaign creation is done navigate to the "old" autosave flow
    // and specify next step because creation flow actually fill in Basic step
    if (isCreateCampaignFlow && campaignId) {
      navigate(
        `${generatePath(RouteList.Campaign, { campaignId: String(campaignId) })}?startStep=${CampaignStep.Creatives}`
      );
    }
  };

  const formReturn = useForm<CampaignFormInputs>({ defaultValues: defaultFormValues, mode: 'onChange' });
  const { control, watch, trigger, clearErrors, reset, getValues, setValue } = formReturn;

  const websitesFieldArray = useFieldArray({ name: CampaignFields.websites, control });

  const createCampaign = async () => {
    let {
      name,
      budget,
      websites,
      campaignPeriod: [start, end],
    } = watch();
    start ||= DateTime.local().plus({ days: MIN_DAYS_BEFORE_CAMPAIGN_START }).startOf('day');
    end ||= DateTime.local().plus({ days: MIN_DAYS_BEFORE_CAMPAIGN_START + 1 });

    const res = await createCampaignMutation.mutateAsync({
      name,
      budget: Number(budget) || 0,
      websites: websites.map((x) => ({ url: x.url })).filter((x) => x.url),
      startDate: start.toJSDate().toISOString(),
      endDate: end.endOf('day').toJSDate().toISOString(),
    });
    return res.id;
  };

  // Local Storage Sync
  const defaultStorageValue: StepStatesByCampaignId = {
    [currentCampaignId]: getStepStates(stepsData),
  };
  const [storageStepsState, setStorageStepsState] = useLocalStorage('campaigns.step.states', defaultStorageValue);

  const onSubmit = () => {
    const { campaignPeriod } = watch();
    const minStartDate = DateTime.local().plus({ days: MIN_DAYS_BEFORE_CAMPAIGN_START });

    const isStartDateValid = campaignPeriod[0]
      ? Interval.fromDateTimes(DateTime.local().startOf('day'), minStartDate.startOf('day')).isBefore(
          campaignPeriod[0].startOf('day')
        )
      : false;

    if (!isStartDateValid) {
      setHasInThePastError(true);
    } else if (hasCreativesWarning) {
      setHasCreativesError(true);
    } else {
      publishCampaignMutation.mutate();
    }
  };

  const resolveStep = (stepOrIndex: number | CampaignStep): CampaignStep =>
    typeof stepOrIndex === 'number' ? orderedSteps[stepOrIndex]! : stepOrIndex;

  const updateStorageStepsState = useCallback(
    (state: CampaignStepState, stepOrIndex?: number | CampaignStep) => {
      const campaignStepStates = getStepStates(stepsData);
      if (stepOrIndex !== undefined) {
        const step = resolveStep(stepOrIndex);
        campaignStepStates[step] = state;
      }
      const newStorageSteps = {
        ...storageStepsState,
        [currentCampaignId]: campaignStepStates,
      };
      setStorageStepsState(newStorageSteps);
    },
    [currentCampaignId, setStorageStepsState, stepsData, storageStepsState]
  );

  // validations
  const validateStep = async (stepOrIndex: number | CampaignStep) => {
    const step = resolveStep(stepOrIndex);
    const stepData = stepsData[step];
    return stepData ? await trigger(stepData.fields) : false;
  };

  const validateWebsite = useCallback(
    async (index: number): Promise<boolean> => {
      if (await trigger(`${CampaignFields.websites}.${index}.url`)) return true;
      if (watch(`${CampaignFields.websites}.${index}.isEditing`)) await focusWebsiteUrl(index);
      return false;
    },
    [trigger, watch]
  );

  const validateField = async (field: CampaignFields) => await trigger(field);

  const validateCreatives = async (index: number) => await trigger(`${CampaignFields.websites}.${index}.creatives`);

  const markStepAsDone = (targetStep: CampaignStep = activeStep) => {
    setStepsData((prev) => {
      const newSteps = { ...prev };
      newSteps[targetStep].state = CampaignStepState.Complete;
      return newSteps;
    });
    updateStorageStepsState(CampaignStepState.Complete, targetStep);
  };

  const getIsStepValid = (step: CampaignStep) => {
    return stepsData[step].state === CampaignStepState.Complete;
  };

  const updateStepsState = useCallback(
    (value: CampaignStepState, stepOrIndex: number | CampaignStep) => {
      const step = resolveStep(stepOrIndex);
      setStepsData((prev) => {
        const newSteps = { ...prev };
        const stepData = newSteps[step];
        if (stepData) {
          stepData.state = value;
        }
        return newSteps;
      });
      updateStorageStepsState(value, step);
    },
    [updateStorageStepsState]
  );

  const hasCreativesWarning = stepsData[CampaignStep.Creatives].state === CampaignStepState.Warning;

  const validateByCustomRule = useCallback(
    (stepOrIndex: CampaignStep | number) => {
      const step = resolveStep(stepOrIndex);
      const { ga4PropertyId, websites } = watch();
      switch (step) {
        case CampaignStep.Creatives: {
          const isCreativeStepValid = websites.every((site) => site.creatives.length);
          if (!isCreativeStepValid) {
            updateStepsState(CampaignStepState.Warning, getStepIndex(CampaignStep.Creatives));
            return CampaignStepState.Warning;
          } else {
            updateStepsState(CampaignStepState.Complete, getStepIndex(CampaignStep.Creatives));
            return CampaignStepState.Complete;
          }
        }
        case CampaignStep.GA: {
          const isGaStepValid = !!ga4PropertyId && !!getCampaignQuery.data?.campaign?.ga4ConnectionValid;
          if (!isGaStepValid) {
            updateStepsState(CampaignStepState.Wrong, getStepIndex(CampaignStep.GA));
            return CampaignStepState.Wrong;
          }
          return CampaignStepState.Complete;
        }
        default:
          return CampaignStepState.Complete;
      }
    },
    [getCampaignQuery.data?.campaign?.ga4ConnectionValid, updateStepsState, watch]
  );

  // API sync (get & update)

  useEffect(() => {
    const { websites: currentWebsites } = watch();

    const creativeStep = stepsData[CampaignStep.Creatives];
    invariant(creativeStep, 'no creativeStep');
    creativeStep.fields = currentWebsites.map((_, index) => `${CampaignFields.websites}.${index}.creatives`) as any;

    if (getCampaignQuery.data?.campaign) {
      const { campaign } = getCampaignQuery.data;

      if (!isInitialized) {
        const currentCampaignValues = getFormInputs({ campaign, user });

        reset(currentCampaignValues);

        Sentry.setContext('campaign', {
          campaignId: campaign.id,
        });

        //initial check on the GA4 conection in case the user changed the property and did not initiated the check
        checkGa4ConnectionMutation.mutate({ sendSuccessEmail: false });

        // set validation fields depending on actual websites
        const currentCampaignStepStates = storageStepsState?.[currentCampaignId];
        if (currentCampaignStepStates) {
          const newSteps = orderedSteps.reduce((acc, step) => {
            acc[step] = {
              ...stepsData[step],
              state: currentCampaignStepStates[step],
            } as any;
            return acc;
          }, {} as CampaignFormStepDataMap);
          setStepsData(newSteps);
          // only run creatives re-validation if it's state is not default to prevent it to be set to warning for no reason
          if (newSteps[CampaignStep.Creatives].state !== CampaignStepState.Default) {
            validateByCustomRule(CampaignStep.Creatives);
          }
        } else {
          updateStorageStepsState(CampaignStepState.Default);
        }
        setIsInitialized(true);
      } else {
        const websitesToUpdate = !campaign.websites.length
          ? defaultFormValues.websites
          : campaign.websites.map(({ url, id, targetingGuidance }) => {
              const targetWebsite = currentWebsites.find((site) => site.url === url);

              return {
                ...targetWebsite,
                url,
                dbId: id,
                creatives: campaign.creatives.filter((creative) => creative.websiteId === id),
                targetingGuidance,
              } as Website;
            });

        setValue(CampaignFields.websites, websitesToUpdate);
      }
    }
    // excludeing checkGa4ConnectionMutation - to avoid rerenders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCampaignId,
    getCampaignQuery.data,
    isInitialized,
    reset,
    setValue,
    stepsData,
    storageStepsState,
    updateStorageStepsState,
    user,
    validateByCustomRule,
    watch,
  ]);

  const saveDebounced = useSlidingDelay(
    async ({ signal }: OnCallOptions) => {
      const currentValues = getValues();
      const {
        name,
        campaignPeriod,
        budget,
        billingMethod,
        maxUnitPrice,
        ga4PropertyId,
        websites,

        targetingGuidance,
        targetingGuidanceConfig,
        targetingGuidanceMode,
        targetingGuidanceFormat,

        billingAddressSalutation,
        billingAddressFirstName,
        billingAddressLastName,
        billingAddressLine1,
        billingAddressLine2,
        billingAddressCountry,
        billingAddressPostcode,
        billingAddressCity,
        billingAddressEmail,
        billingAddressPhone,
        billingAddressCompanyName,

        billingAddressEqualToContact,

        contactAddressSalutation,
        contactAddressFirstName,
        contactAddressLastName,
        contactAddressLine1,
        contactAddressLine2,
        contactAddressCountry,
        contactAddressPostcode,
        contactAddressCity,
        contactAddressEmail,
        contactAddressPhone,
        contactAddressCompanyName,
      } = currentValues;

      // campaignPeriod
      const startDate = DateTime.fromISO(String(campaignPeriod[0]));
      const endDate = DateTime.fromISO(String(campaignPeriod[1]?.endOf('day')));

      if (!startDate.isValid || !endDate.isValid) {
        throw new Error('Invalid start and date: ' + [startDate.invalidReason, endDate.invalidReason].join(', '));
      }

      // websites
      const websitesToUpdate = websites
        .filter((item) => !!item.url)
        .map(({ url, dbId, targetingGuidance, targetingGuidanceConfig }): WebsiteRequestItem => {
          return {
            url,
            id: dbId,
            targetingGuidance: targetingGuidance || '',
            targetingGuidanceConfig: targetingGuidanceConfig || defaultTargetingGuidanceConfig,
          };
        });

      const websiteValidateResults = await Promise.all(
        websites.map(async ({ url }, i) => {
          if (!url || (await validateWebsite(i))) return true;

          internalRef.current.setWebsiteEditing(i, true);
          return false;
        })
      );

      if (!name || websiteValidateResults.some((isValid) => !isValid)) return;

      const updatePayload: UpdateCampaignRequest = {
        ...defaultUpdateRequestBody,
        name,
        billingMethod,
        startDate: startDate.toJSDate().toISOString(),
        endDate: endDate.toJSDate().toISOString(),
        ga4PropertyId,
        maxUnitPrice: maxUnitPrice || null,
        budget: Number(budget) || 0,
        websites: websitesToUpdate,

        // do not send both website and campaign targeting guidance in order to avoid conflicts
        targetingGuidance,
        targetingGuidanceConfig,
        targetingGuidanceMode,
        targetingGuidanceFormat,

        billingAddressSalutation: billingAddressEqualToContact ? contactAddressSalutation : billingAddressSalutation,
        billingAddressFirstName: billingAddressEqualToContact ? contactAddressFirstName : billingAddressFirstName,
        billingAddressLastName: billingAddressEqualToContact ? contactAddressLastName : billingAddressLastName,
        billingAddressLine1: billingAddressEqualToContact ? contactAddressLine1 : billingAddressLine1,
        billingAddressLine2: billingAddressEqualToContact ? contactAddressLine2 : billingAddressLine2,
        billingAddressCountry: billingAddressEqualToContact ? contactAddressCountry : billingAddressCountry,
        billingAddressPostcode: billingAddressEqualToContact ? contactAddressPostcode : billingAddressPostcode,
        billingAddressCity: billingAddressEqualToContact ? contactAddressCity : billingAddressCity,
        billingAddressEmail: billingAddressEqualToContact ? contactAddressEmail : billingAddressEmail,
        billingAddressPhone: billingAddressEqualToContact ? contactAddressPhone : billingAddressPhone,
        billingAddressCompanyName: billingAddressEqualToContact ? contactAddressCompanyName : billingAddressCompanyName,

        contactAddressSalutation,
        contactAddressFirstName,
        contactAddressLastName,
        contactAddressLine1,
        contactAddressLine2,
        contactAddressCountry,
        contactAddressPostcode,
        contactAddressCity,
        contactAddressEmail,
        contactAddressPhone,
        contactAddressCompanyName,
      };

      /**
       * See the flaky case here: {@link https://github.com/alloy-ch/rcplus-alloy-selfservice-frontend/issues/280}
       *
       * STR:
       *
       *   1. Setup a valid campaign so the FE will auto-save
       *   2. Type something in e.g. the optimization guidance
       *   3. Wait until form auto-saves
       *   4. Quickly start typing into the box
       *
       * Expected:
       *   What i type in the box stays
       *
       * Actual:
       *   What i typed in disappears
       *
       * Explanation:
       *   The classic save debounced pattern mostly works fine, but at some entry rate, the server responds with
       *   the save success response AFTER the further changes were scheduled for save, hence overwriting them.
       *
       * Marbles (1 frame = 200 ms):
       *
       *   * Form defaults [1]: 0---------------------b
       *   * User types:        ----a-b------c
       *   * onFieldChange:     ----a-b------c
       *   * saveDebounced [2]:       \---b  \---c
       *   * mutateAsync:                 b      c
       *   * onSuccess [3]:               \-----------b
       *                                         \-----------c
       *                                              ^ here we lose the user's entry 'c', which is rewritten with the actual backend state 'b'
       *
       * [1]: The form is reset with the initial data or the campaign db state. This effectively discards dirty fields and all unsaved changes.
       * [2]: saveDebounced defers API update request by 800 ms.
       * [3]: API request duration varies, so the bug is not always reproducible. However, theoretically, at some rate, it is possible to lose every keystroke!
       *
       * Solution:
       *   1. QueryClient as a source of truth should always contain the most recent data, which is reflected in UI. See {@link useCampaign}.
       *   2. Advanced debounce flow (see {@link useSlidingDelay}):
       *     - aborts started update when a newer data arrives;
       *     - respects whether the data is newer than the server and schedules an update request, if needed.
       *   3. The worker function saveDebounced (right here) accepts the abort signal to cancel stale network requests,
       *      and resets the error flag in updateCampaignMutation when the next request is expected.
       */
      try {
        await updateCampaignMutation.mutateAsync(
          {
            payload: updatePayload,
            signal,
          },
          {
            onSuccess() {
              reset(currentValues);
            },
            onError(err) {
              // RUNNING_ABORTED means that new a new task is going to start after the debounce delay, and this one is discarded
              if (SlidingDelayError.isCode(err, 'RUNNING_ABORTED')) {
                // since the next update task is going to start, we are safe to reset isError flag explicitly
                updateCampaignMutation.reset();
              }
            },
          }
        );
      } catch (err) {
        // log unexpected exception
        Sentry.captureException(err);
      }
    },
    saveDelay,
    { abortRunning: true, suppressRejections: true }
  );

  const saveCampaign = saveDebounced.immediate;

  const onFieldChange = <F extends CampaignFormInputsPath>(fieldName: F, immediate: boolean = false) => {
    clearErrors(fieldName);
    // disable autosave if we are on Basic step of campaignCreateFlow
    if (!isCreateCampaignFlow) {
      if (immediate) saveCampaign();
      else saveDebounced();
    }
  };

  const watchFieldArray = watch(CampaignFields.websites);

  const websites = websitesFieldArray.fields.map(
    (field, index): CampaignFormField<CampaignFields.websites> => ({ ...field, ...watchFieldArray[index] })
  );

  const saveWebsites = async () => {
    websitesFieldArray.replace(websites.filter((e) => e.url).map((e) => ({ ...e, isEditing: false })));
    return saveDebounced();
  };

  const addNewWebsite = async () => {
    websitesFieldArray.append(emptyWebsite);
    await focusWebsiteUrl(websitesFieldArray.fields.length);
  };

  const removeWebsite = async (index: number) => {
    const anyOther = websites.length > 1;
    websitesFieldArray.remove(index);
    await saveCampaign();
    if (anyOther) {
      const websites = watch(CampaignFields.websites);
      const prevEditingIndex = websites.findLastIndex((item) => item?.isEditing, index);
      prevEditingIndex >= 0 && (await focusWebsiteUrl(prevEditingIndex));
    } else {
      addNewWebsite();
    }
  };

  const updateWebsite = useCallback(
    async (index: number, changes: Partial<Website>) => {
      const website = websites[index];
      if (!website) return;
      websitesFieldArray.update(index, { ...website, ...changes });
      if (await validateWebsite(index)) await saveCampaign();
    },
    [saveCampaign, validateWebsite, websites, websitesFieldArray]
  );

  function setWebsiteEditing(index: number, isEditing: boolean) {
    // TODO: remove type assertion once react-hook-form fixed FieldPathValue for dynamic fields
    setValue(`${CampaignFields.websites}.${index}.isEditing`, isEditing as never, { shouldDirty: !isEditing });
  }

  // navigation

  const isClickBilling = watch(CampaignFields.billingMethod) === BillingMethod.CLICK;

  const goToNextStep = async () => {
    if (activeStep === CampaignStep.Complete) return;

    let isCurrentStepValid = await validateStep(activeStep);

    setIsPreNavValidation(true);
    if (activeStep === CampaignStep.GA && !isClickBilling) {
      const { successful } = await checkGa4ConnectionMutation.mutateAsync({
        sendSuccessEmail: false,
        shouldCount: true,
      });
      if (!successful) {
        isCurrentStepValid = false;
      }
    }

    if (isCurrentStepValid) {
      let campaignId = null;
      const isCurrentStepWarned = stepsData[activeStep].state === CampaignStepState.Warning;
      const shouldSkipStep = activeStep === CampaignStep.KPI && isClickBilling;

      if (!isCreateCampaignFlow) {
        // calls saveDebounced
        saveWebsites();
      }

      const targetStep = getNextStep(activeStep, shouldSkipStep ? 2 : 1);
      if (!targetStep) return;

      if (isCreateCampaignFlow) {
        campaignId = await createCampaign();
      } else {
        // forced save
        await saveCampaign();
      }

      // handle change step do navigate if campaignCreationFlow enabled which "skips" markStepAsDone
      // we need to mark step first and then handle step change
      !isCurrentStepWarned && markStepAsDone();
      handleChangeStep(targetStep, campaignId);
    }
    setIsPreNavValidation(false);
  };

  const goToPrevStep = async () => {
    if (isFirstStep) {
      navigate(RouteList.MyCampaigns);
      return;
    }

    // Ensure address fields are saved immediately when leaving Complete step
    if (activeStep === CampaignStep.Complete) {
      await saveCampaign();
    }

    const shouldSkipStep = activeStep === CampaignStep.Complete && isClickBilling;
    const targetStep = getNextStep(activeStep, shouldSkipStep ? -2 : -1);
    invariant(targetStep, `no targetStep, activeStep ${activeStep}, advanceBy ${shouldSkipStep ? -2 : -1}`);
    await redirectToStep(targetStep);
  };

  const redirectToStep = async (step: CampaignStep) => {
    invariant(step, 'no step');

    // Ensure address fields are saved immediately when leaving Complete step
    if (activeStep === CampaignStep.Complete && step !== activeStep) {
      await saveCampaign();
    }

    if (stepIndices[step] < stepIndices[activeStep]) {
      handleChangeStep(step);
      return;
    }

    const isCurrentStepValid = await validateStep(activeStep);
    let isTargetStepAvailable = true;

    for (let i = stepIndices[activeStep]; i <= stepIndices[step]; i++) {
      const nextStep = orderedSteps[i];
      if (!nextStep) continue;
      const isStepValid = await validateStep(nextStep);
      switch (nextStep) {
        case CampaignStep.Creatives: {
          validateByCustomRule(CampaignStep.Creatives);
          break;
        }
        case CampaignStep.Targeting: {
          if (activeStep !== CampaignStep.Targeting) {
            await saveCampaign();
          }
          if (validateByCustomRule(CampaignStep.Targeting) === CampaignStepState.Wrong) {
            handleChangeStep(CampaignStep.Targeting);
            return;
          }
          break;
        }
        case CampaignStep.GA: {
          if (validateByCustomRule(CampaignStep.GA) === CampaignStepState.Wrong && !isClickBilling) {
            handleChangeStep(CampaignStep.GA);
            return;
          }
          break;
        }
        case CampaignStep.Complete: {
          const { successful } = await checkGa4ConnectionMutation.mutateAsync({
            sendSuccessEmail: false,
            shouldCount: true,
          });
          if (!successful && !isClickBilling) {
            handleChangeStep(CampaignStep.GA);
            return;
          }
          break;
        }
      }
      if (!isStepValid) {
        isTargetStepAvailable = false;
        return; // TODO: return or break???
      }
      const nextStepData = stepsData[nextStep];
      if (nextStepData && nextStepData.state !== CampaignStepState.Warning) {
        markStepAsDone(nextStep);
      }
    }

    const canGoToStep = isCurrentStepValid && isTargetStepAvailable;

    if (canGoToStep) {
      stepsData[activeStep].state !== CampaignStepState.Warning && markStepAsDone();

      // update sites state if it was a basic step and enqueue save
      if (activeStep === CampaignStep.Basic) saveWebsites();

      // forced save
      await saveCampaign();

      handleChangeStep(step);
    }
  };

  const getFieldControls: GetFieldControls = (name, { shouldSkipOnChange, ...options } = {}) =>
    control.register(name, {
      ...options,
      onChange: () => (shouldSkipOnChange ? null : onFieldChange(name)),
    });

  return {
    formReturn,

    campaignContext: {
      isInitialized,
      activeStep,
      isFirstStep,
      isLastStep,
      stepsData,
      redirectToStep,
      goToPrevStep,
      goToNextStep,
      getIsStepValid,
      updateStepsState,

      onSubmit,
      onFieldChange,
      getFieldControls,

      isPreNavValidation,
      validateCreatives,
      validateWebsite,
      validateField,

      //GA4
      isConnectionSuccess,
      setIsConnectionSuccess,
      isConnectionSuccessCampaignState: getCampaignQuery.data?.campaign?.ga4ConnectionValid,
      failureCount,
      checkGa4ConnectionMutation,
      getGa4CampaignUrlQuery,

      websites,
      updateWebsite,
      addNewWebsite,
      removeWebsite,
      setWebsiteEditing,

      hasCreativesWarning,

      hasCreativesError,
      hasInThePastError,
      setHasInThePastError,
      isCreateCampaignFlow,

      ...formReturn,
    },
  } as const;
};

export const campaignFormContext = createContext({} as CampaignFormContext);

export const CampaignFormProvider = ({
  children,
  isCreateCampaignFlow,
  startStep,
  saveDelay,
}: PropsWithChildren<CampaignFormProviderProps>) => {
  const { campaignContext, formReturn } = GetCampaignFormContext({ isCreateCampaignFlow, startStep, saveDelay });
  return (
    <campaignFormContext.Provider value={campaignContext}>
      <FormProvider<CampaignFormInputs> {...formReturn}>{children}</FormProvider>
    </campaignFormContext.Provider>
  );
};
